/**
 * Configuracoes persistidas.
 *
 * Cada plugin recebe um espaco de nomes proprio (`ctx.settings`), pelo que nunca
 * precisa de prefixar chaves nem corre o risco de pisar as de outro plugin.
 *
 * O nucleo guarda aqui tambem a `LocaleConfig` da familia. E a razao de a regra
 * 10 funcionar: as telas escrevem portugues a mao, mas numeros, datas e moeda
 * saem sempre de `@flos/shared/locale`, que le desta definicao. Mudar de pt-BR
 * para pt-PT e mudar um valor, nao reescrever telas.
 */
import {
  DEFAULT_LOCALE,
  setLocaleConfig,
  type LocaleConfig,
  type Logger,
  type SettingsLike,
} from '@flos/shared';
import type { FlosDatabase, SettingsRow } from './db';
import { CoreLocalEvents, type EventBus } from './events';

export const CORE_SETTINGS_NAMESPACE = 'core';
export const LOCALE_SETTING_KEY = 'locale';

export interface SettingsChangedPayload {
  namespace: string;
  key: string;
  value: unknown;
}

function composeKey(namespace: string, key: string): string {
  return `${namespace}:${key}`;
}

export class SettingsService {
  readonly #db: FlosDatabase;
  readonly #events: EventBus | undefined;
  readonly #logger: Logger | undefined;
  /** Cache completa em memoria: sao poucas dezenas de chaves e permite leitura
   *  sincrona onde a assincrona nao cabe (formatadores, render). */
  readonly #cache = new Map<string, unknown>();
  readonly #defaults = new Map<string, unknown>();
  #loaded = false;

  constructor(db: FlosDatabase, events?: EventBus, logger?: Logger) {
    this.#db = db;
    this.#events = events;
    this.#logger = logger;
  }

  /** Le tudo do disco e aplica a configuracao regional. Chamar uma vez, no arranque. */
  async load(): Promise<void> {
    const rows = await this.#db.settingsTable.toArray();
    this.#cache.clear();
    for (const row of rows) this.#cache.set(row.key, row.value);
    this.#loaded = true;
    setLocaleConfig(this.locale);
    this.#logger?.debug('Configuracoes carregadas', { count: rows.length });
  }

  get loaded(): boolean {
    return this.#loaded;
  }

  /** Valores de origem de um plugin. Nao sao gravados: so preenchem falhas. */
  registerDefaults(namespace: string, defaults: Record<string, unknown>): void {
    for (const [key, value] of Object.entries(defaults)) {
      this.#defaults.set(composeKey(namespace, key), value);
    }
  }

  async get<T>(namespace: string, key: string, fallback: T): Promise<T> {
    return this.getSync(namespace, key, fallback);
  }

  /** Versao sincrona, valida depois de `load()`. */
  getSync<T>(namespace: string, key: string, fallback: T): T {
    const composed = composeKey(namespace, key);
    if (this.#cache.has(composed)) return this.#cache.get(composed) as T;
    if (this.#defaults.has(composed)) return this.#defaults.get(composed) as T;
    return fallback;
  }

  async set<T>(namespace: string, key: string, value: T): Promise<void> {
    const composed = composeKey(namespace, key);
    const row: SettingsRow = {
      key: composed,
      namespace,
      name: key,
      value,
      updatedAt: new Date().toISOString(),
    };
    await this.#db.settingsTable.put(row);
    this.#cache.set(composed, value);
    const payload: SettingsChangedPayload = { namespace, key, value };
    this.#events?.emit(CoreLocalEvents.SETTINGS_CHANGED, payload);
  }

  async remove(namespace: string, key: string): Promise<void> {
    const composed = composeKey(namespace, key);
    await this.#db.settingsTable.delete(composed);
    this.#cache.delete(composed);
    const payload: SettingsChangedPayload = { namespace, key, value: undefined };
    this.#events?.emit(CoreLocalEvents.SETTINGS_CHANGED, payload);
  }

  /** Todas as chaves de um espaco de nomes, defaults incluidos. */
  all(namespace: string): Record<string, unknown> {
    const prefix = `${namespace}:`;
    const out: Record<string, unknown> = {};
    for (const [composed, value] of this.#defaults) {
      if (composed.startsWith(prefix)) out[composed.slice(prefix.length)] = value;
    }
    for (const [composed, value] of this.#cache) {
      if (composed.startsWith(prefix)) out[composed.slice(prefix.length)] = value;
    }
    return out;
  }

  /** Vista com escopo, que e o que o plugin recebe em `ctx.settings`. */
  namespace(namespace: string): SettingsLike {
    return {
      get: <T>(key: string, fallback: T) => this.get(namespace, key, fallback),
      set: <T>(key: string, value: T) => this.set(namespace, key, value),
    };
  }

  // --- Configuracao regional da familia ---

  get locale(): LocaleConfig {
    return this.getSync(CORE_SETTINGS_NAMESPACE, LOCALE_SETTING_KEY, DEFAULT_LOCALE);
  }

  async setLocale(config: LocaleConfig): Promise<void> {
    await this.set(CORE_SETTINGS_NAMESPACE, LOCALE_SETTING_KEY, config);
    // Aplicar de imediato: os formatadores em `@flos/shared` tem cache e so a
    // limpam quando esta funcao e chamada.
    setLocaleConfig(config);
    this.#events?.emit(CoreLocalEvents.LOCALE_CHANGED, config);
    this.#logger?.info('Configuracao regional alterada', {
      locale: config.locale,
      currency: config.currency,
    });
  }
}
